import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Mail, Phone, MapPin, CalendarIcon } from 'lucide-react';
import { LabeledInput } from '@/components/shared/LabeledInput';
import { SearchableLabeledSelect } from '@/components/shared/SearchableLabeledSelect';
import { LabeledSelect } from '@/components/shared/LabeledSelect';
import { getFullName, getClientInitials } from '@/services/clientDataService';
import { Client } from '@/types/client';
import { formatDateShort } from '@/utils/date-utils';
import countryOptions from '@/data/countries.json';

const MEASUREMENT_SYSTEM_OPTIONS = [
  { value: 'metric', label: 'Metric (kg, cm, °C)' },
  { value: 'imperial', label: 'Imperial (lbs, ft/in, °F)' }
];

interface ClientBasicDetailsProps {
  client: Client;
  isEditMode: boolean;
  editData: {
    dateOfBirth?: string;
    location?: string;
    preferredMeasurementSystem?: string;
  };
  onDataChange: (field: string, value: any) => void;
}

export const ClientBasicDetails: React.FC<ClientBasicDetailsProps> = ({
  client,
  isEditMode,
  editData,
  onDataChange,
}) => {

  return (
    <Card className="lg:col-span-3 shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-background to-muted/20">
      <CardContent className="pt-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left side - Avatar and Basic Info */}
          <div className="flex items-start gap-4">
            <div className="relative">
              <Avatar className="h-16 w-16 ring-4 ring-primary/10">
                <AvatarImage src="" alt={getFullName(client)} />
                <AvatarFallback className="text-lg bg-gradient-to-br from-primary/20 to-primary/10 text-primary font-bold">
                  {getClientInitials(client)}
                </AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-1 -right-1 h-5 w-5 bg-primary rounded-full border-2 border-background flex items-center justify-center">
                <div className="h-1.5 w-1.5 bg-background rounded-full"></div>
              </div>
            </div>

            <div className="space-y-2">
              <h2 className="text-xl font-bold text-foreground">{getFullName(client)}</h2>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Mail className="h-4 w-4 flex-shrink-0" />
                  <span className="break-all">{client.email}</span>
                </p>
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Phone className="h-4 w-4 flex-shrink-0" />
                  <span>{client.phone || 'No phone number'}</span>
                </p>
              </div>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium shadow-sm ${
                client.status === 'active'
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-amber-50 text-amber-700 border border-amber-200'
              }`}>
                {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
              </span>
            </div>
          </div>

          {/* Right side - Date of Birth and Location */}
          <div className="lg:ml-auto space-y-4">
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Date of Birth</p>
              {isEditMode ? (
                <LabeledInput
                  label=""
                  type="date"
                  value={editData.dateOfBirth 
                    ? new Date(editData.dateOfBirth).toISOString().split('T')[0] 
                    : (client.dateOfBirth ? new Date(client.dateOfBirth).toISOString().split('T')[0] : '')}
                  onChange={(e) => onDataChange('dateOfBirth', e.target.value)}
                  icon={<CalendarIcon className="h-4 w-4" />}
                  className="h-10 text-sm transition-all duration-300 focus-within:ring-2 focus-within:ring-primary/30 hover:bg-primary/5 border-border/50 hover:border-primary/30"
                  labelClassName="hidden"
                />
              ) : (
                <p className="text-sm font-semibold text-foreground">
                  {client.dateOfBirth ? formatDateShort(client.dateOfBirth) : 'Not specified'}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Location</p>
              {isEditMode ? (
                <SearchableLabeledSelect
                  label=""
                  value={editData.location || ''}
                  onChange={(value) => onDataChange('location', value)}
                  options={countryOptions}
                  placeholder="Select location"
                  searchPlaceholder="Search countries..."
                  wrapperClassName="space-y-0"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm font-semibold text-foreground">{client.location || 'Not specified'}</p>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Measurement System</p>
              {isEditMode ? (
                <LabeledSelect
                  label=""
                  options={MEASUREMENT_SYSTEM_OPTIONS}
                  value={editData.preferredMeasurementSystem || client.preferredMeasurementSystem || 'metric'}
                  onChange={(value) => onDataChange('preferredMeasurementSystem', value)}
                  placeholder="Select measurement system"
                  wrapperClassName="space-y-0"
                />
              ) : (
                <p className="text-sm font-semibold text-foreground">
                  {client.preferredMeasurementSystem === 'imperial' ? 'Imperial (lbs, ft/in, °F)' : 'Metric (kg, cm, °C)'}
                </p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
